import { useMutation, useQuery } from '@apollo/client';
import CircularProgress from '@mui/material/CircularProgress';
import { useRef, useState } from 'react';

import { apiErrorMessage } from '@/core/api';
import { useAppSelector } from '@/store';

import type { Notice } from './Banner';
import InvitationBatchView, { invitationCount } from './InvitationBatchView';
import LoadError from './LoadError';
import {
  CANCEL_CANDIDATE_INVITATION_BATCH,
  GET_CANDIDATE_INVITATION_BATCH,
  SEND_CANDIDATE_INVITATION_BATCH,
} from './operations';

interface CandidateInvitationBatchPageProps {
  /** From the URL; only used to link back when the batch cannot be shown. */
  expertRequestId: string;
  token: string;
}

// A failed send may still have gone out (a timeout after the api committed), so
// the page re-reads the batch and says to check it rather than to retry.
const SEND_FAILED =
  'The invitations could not be confirmed as sent. Check their state below before trying again.';
const CANCEL_FAILED = 'The invitations could not be cancelled. Try again.';

function sentMessage(sent: number, prepared: number): string {
  const skipped = prepared - sent;
  if (skipped <= 0) return `Sent ${invitationCount(sent)}.`;
  return `Sent ${invitationCount(sent)}. ${skipped} skipped because they can no longer be invited (they left the request or were rejected).`;
}

export default function CandidateInvitationBatchPage({
  expertRequestId,
  token,
}: CandidateInvitationBatchPageProps): JSX.Element {
  const timezone = useAppSelector((state) => state.viewer.timezone);
  const variables = { token };
  // network-only: the buttons act on the batch's state, so never offer them
  // from a cached read that another tab may have outdated.
  const { data, loading, error, refetch } = useQuery(GET_CANDIDATE_INVITATION_BATCH, {
    variables,
    fetchPolicy: 'network-only',
  });
  const [send, sendResult] = useMutation(SEND_CANDIDATE_INVITATION_BATCH);
  const [cancel, cancelResult] = useMutation(CANCEL_CANDIDATE_INVITATION_BATCH);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Set from the click until the page shows where the batch ended up, re-read
  // included, so neither button is offered against a state that is out of date.
  const [action, setAction] = useState<'send' | 'cancel' | null>(null);
  // `action` only disables the buttons after a re-render; this closes the gap
  // in which a fast double click could fire the mutation twice.
  const inFlight = useRef(false);

  // The mutations return the batch in the state they reached, which is newer
  // than the query's read.
  const batch =
    sendResult.data?.sendCandidateInvitationBatch ??
    cancelResult.data?.cancelCandidateInvitationBatch ??
    data?.candidateInvitationBatch;

  // A failed re-read leaves the last state shown; the notice already says what failed.
  async function reread() {
    await refetch().catch(() => undefined);
  }

  async function handleSend() {
    if (inFlight.current) return;
    inFlight.current = true;
    setAction('send');
    setNotice(null);
    const prepared = batch?.recipients.length ?? 0;
    try {
      const result = await send({ variables });
      const sent = result.data?.sendCandidateInvitationBatch;
      if (sent) setNotice({ tone: 'success', text: sentMessage(sent.recipients.length, prepared) });
      else await reread();
    } catch (err) {
      setNotice({ tone: 'error', text: apiErrorMessage(err, SEND_FAILED) });
      await reread();
    } finally {
      inFlight.current = false;
      setAction(null);
    }
  }

  async function handleCancel() {
    if (inFlight.current) return;
    inFlight.current = true;
    setAction('cancel');
    setNotice(null);
    try {
      const result = await cancel({ variables });
      if (!result.data?.cancelCandidateInvitationBatch) await reread();
    } catch (err) {
      setNotice({ tone: 'error', text: apiErrorMessage(err, CANCEL_FAILED) });
      await reread();
    } finally {
      inFlight.current = false;
      setAction(null);
    }
  }

  if (loading && !batch) {
    return (
      <div className="flex justify-center py-40">
        <CircularProgress aria-label="Loading invitations" />
      </div>
    );
  }

  if (error || !batch) return <LoadError error={error} expertRequestId={expertRequestId} />;

  return (
    <InvitationBatchView
      batch={batch}
      timezone={timezone}
      action={action}
      notice={notice}
      onSend={handleSend}
      onCancel={handleCancel}
    />
  );
}
